import * as React from "react";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { convertToPoint, IInputCoordinates, validateCoords } from "../Coordinates/CoordinateUtils";
import { useConvertContext } from "../../Contexts/ConvertContext";
import { useMapContext } from "../../Contexts/MapContext";
import "./CoordinateInput.scss";

const xArrow = "<span class='arrow-east'>&#10141;</span>";
const yArrow = "<span class='arrow-north'>&#10141;</span>";

export const CoordinateInput = (): JSX.Element => {
    const { setPlacing } = useMapContext();
    const { formatType, setCoordinates, inputCoordinates, setPinPlaced, setCoordinatesInvalid } = useConvertContext();
    const [value, setValue] = useState<IInputCoordinates>(inputCoordinates);

    const saveCoordinates = (coordinates: IInputCoordinates) => {
        setValue({ format: formatType, x: coordinates.x, y: coordinates.y });
        if (validateCoords(coordinates, formatType)) {
            const coords = convertToPoint(formatType, coordinates);
            if (coords) {
                if (coords !== "invalidMGRS") {
                    coords.x && setCoordinates([coords.x, coords.y]);
                    setCoordinatesInvalid(false);
                    setPlacing(true);
                } else {
                    setCoordinatesInvalid(true);
                    setPinPlaced(false);
                }
            }
        } else {
            setCoordinatesInvalid(true);
            setPinPlaced(false);
        }
    };

    const onInputChange = (evt: any) => {
        const xValues = value.x;
        const yValues = value.y;
        const type = evt.target.name;
        switch (type) {
            case "x":
                xValues[0] = evt.target.value;
                break;
            case "x1":
                xValues[1] = evt.target.value;
                break;
            case "x2":
                xValues[2] = evt.target.value;
                break;
            case "y":
                yValues[0] = evt.target.value;
                break;
            case "y1":
                yValues[1] = evt.target.value;
                break;
            case "y2":
                yValues[2] = evt.target.value;
                break;
        }
        saveCoordinates({ x: xValues, y: yValues } as IInputCoordinates);
    };

    useEffect(() => {
        inputCoordinates.format === "LKSGEO"
            ? setValue({ format: inputCoordinates.format, x: inputCoordinates.y, y: inputCoordinates.x })
            : setValue(inputCoordinates);
    }, [inputCoordinates]);

    return (
        <div className="coordinate-inputs">
            {value.format === "DD" ? (
                <>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "Lat (DD," + yArrow + ")" }}></span>
                    <div className={classNames("x", value.format)}>
                        <InputField val={value.x[0]} name={"x"} onChange={onInputChange} symbol={"°"} />
                    </div>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "Long (DD, " + xArrow + ")" }}></span>
                    <div className={classNames("y", value.format)}>
                        <InputField val={value.y[0]} name={"y"} onChange={onInputChange} symbol={"°"} />
                    </div>
                </>
            ) : value.format === "LKS" ? (
                <>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "x(E) (m," + xArrow + ")" }}></span>
                    <div className={classNames("x", value.format)}>
                        <InputField val={value.x[0]} name={"x"} onChange={onInputChange} />
                    </div>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "y(N) (m, " + yArrow + ")" }}></span>
                    <div className={classNames("y", value.format)}>
                        <InputField val={value.y[0]} name={"y"} onChange={onInputChange} />
                    </div>
                </>
            ) : value.format === "LKSGEO" ? (
                <>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "x(N) (m," + yArrow + ")" }}></span>
                    <div className={classNames("x", value.format)}>
                        <InputField val={value.y[0]} name={"y"} onChange={onInputChange} />
                    </div>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "y(E) (m, " + xArrow + ")" }}></span>
                    <div className={classNames("y", value.format)}>
                        <InputField val={value.x[0]} name={"x"} onChange={onInputChange} />
                    </div>
                </>
            ) : value.format === "DDM" ? (
                <>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "Lat (DM," + yArrow + ")" }}></span>
                    <div className={classNames("x", value.format)}>
                        <InputField val={value.x[0]} name={"x"} onChange={onInputChange} symbol={"°"} />
                        <InputField val={value.x[1]} name={"x1"} onChange={onInputChange} symbol={"'"} />
                    </div>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "Long (DM, " + xArrow + ")" }}></span>
                    <div className={classNames("y", value.format)}>
                        <InputField val={value.y[0]} name={"y"} onChange={onInputChange} symbol={"°"} />
                        <InputField val={value.y[1]} name={"y1"} onChange={onInputChange} symbol={"'"} />
                    </div>
                </>
            ) : value.format === "DMS" ? (
                <>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "Lat (DMS," + yArrow + ")" }}></span>
                    <div className={classNames("x", value.format)}>
                        <InputField val={value.x[0]} name={"x"} onChange={onInputChange} symbol={"°"} />
                        <InputField val={value.x[1]} name={"x1"} onChange={onInputChange} symbol={"'"} />
                        <InputField val={value.x[2]} name={"x2"} onChange={onInputChange} symbol={'"'} />
                    </div>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "Long (DMS, " + xArrow + ")" }}></span>
                    <div className={classNames("y", value.format)}>
                        <InputField val={value.y[0]} name={"y"} onChange={onInputChange} symbol={"°"} />
                        <InputField val={value.y[1]} name={"y1"} onChange={onInputChange} symbol={"'"} />
                        <InputField val={value.y[2]} name={"y2"} onChange={onInputChange} symbol={'"'} />
                    </div>
                </>
            ) : value.format === "MGRS" ? (
                <>
                    <span className="label" dangerouslySetInnerHTML={{ __html: "MGRS" }}></span>
                    <div className={classNames("x", value.format)}>
                        <InputField val={value.x[0]} name={"x"} onChange={onInputChange} isMGRS={true} />
                    </div>
                </>
            ) : null}
        </div>
    );
};

const InputField = ({
    val,
    name,
    onChange,
    symbol,
    isMGRS
}: {
    val: string | number;
    name: string;
    onChange: (e) => void;
    symbol?: string;
    isMGRS?: boolean;
}) => {
    return (
        <>
            <input type={isMGRS ? "text" : "number"} name={name} onChange={onChange} value={val || ""} maxLength={50} />
            {symbol ? <span>{symbol}</span> : null}
        </>
    );
};
